import React, { useState } from 'react';
import { FiX, FiPhone, FiUser, FiMapPin, FiMail } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './addSupplier.css';

const AddSupplier = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        contact_number: '',
        email: '',
        pan_number: '',
        address: '',
        due_amount: '',
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Supplier name is required';
        }
        if (!formData.contact_number.trim()) {
            newErrors.contact_number = 'Contact number is required';
        } else if (!/^[0-9]{10,15}$/.test(formData.contact_number)) {
            newErrors.contact_number = 'Enter a valid contact number (10 digits)';
        }
        if (!formData.address.trim()) {
            newErrors.address = 'Address is required';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Enter a valid email address';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError(null);
        
        if (validateForm()) {
            setIsSubmitting(true);
            try {
                const response = await axios.post('/suppliers', {
                    name: formData.name,
                    contact_number: formData.contact_number,
                    email: formData.email || null,
                    pan_number: formData.pan_number || null,
                    due_amount: formData.due_amount,
                    address: formData.address
                });

                if (response.status === 201) {
                    navigate('/suppliers');
                }
            } catch (err) {
                console.error('Error adding supplier:', err);
                setApiError(err.response?.data?.error || 'Failed to add supplier. Please try again.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <div className="add-supplier-container">
            <div className="add-supplier-header">
                <h1 className="add-supplier-title">New Supplier</h1>
                <button 
                    className="cancel-btn"
                    onClick={() => navigate('/suppliers')}
                    disabled={isSubmitting}
                >
                    <FiX /> Cancel
                </button>
            </div>

            {apiError && (
                <div className="api-error-message">
                    {apiError}
                </div>
            )}

            <form onSubmit={handleSubmit} className="supplier-form">
                <div className="form-section-title">
                    <h3>Supplier Information</h3>
                </div>

                <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
                    <label htmlFor="name">
                        <FiUser className="input-icon" />
                        Supplier Name 
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter supplier name"
                        className={errors.name ? 'error-input' : ''}
                        disabled={isSubmitting}
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className={`form-group ${errors.contact_number ? 'has-error' : ''}`}>
                    <label htmlFor="contact_number">
                        <FiPhone className="input-icon" />
                        Contact Number 
                    </label>
                    <input
                        type="tel"
                        id="contact_number"
                        name="contact_number"
                        value={formData.contact_number}
                        onChange={handleChange}
                        placeholder="Enter 10 digit number"
                        className={errors.contact_number ? 'error-input' : ''}
                        disabled={isSubmitting}
                    />
                    {errors.contact_number && <span className="error-message">{errors.contact_number}</span>}
                </div>

                <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
                    <label htmlFor="email">
                        <FiMail className="input-icon" />
                        Email
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="example@domain.com"
                        className={errors.email ? 'error-input' : ''}
                        disabled={isSubmitting}
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className={'form-group' }>
                  <label htmlFor="email">Pan Number</label>
                  <input
                    id="pan_number"
                    name="pan_number"
                    value={formData.pan_number}
                    onChange={handleChange}
                    placeholder="Enter retailer pan"
                    disabled={isSubmitting}
                  />
                </div>


                <div className={`form-group ${errors.address ? 'has-error' : ''}`}>
                    <label htmlFor="address">
                        <FiMapPin className="input-icon" />
                        Address 
                    </label>
                    <input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Enter full address"
                        className={errors.address ? 'error-input' : ''}
                        disabled={isSubmitting}
                    />
                    {errors.address && <span className="error-message">{errors.address}</span>}
                </div>

                 <div className={'form-group' }>
                  <label htmlFor="email">Opening Balance</label>
                  <input
                    type='number'
                    id="due_amount"
                    name="due_amount"
                    value={formData.due_amount}
                    onChange={handleChange}
                    placeholder="Enter supplier opening balance"
                    disabled={isSubmitting}
                    onWheel={(e) => e.target.blur()}
                  />
                </div>

                <div className="form-actions">
                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner"></span>
                                Saving...
                            </>
                        ) : 'Save Supplier'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddSupplier;